import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { DENIED_SLA_GROUP_NAME, SlaLevelType } from 'constants/slaGroups';
import searchItems from 'utils/searchItems';

import { selectRolesList } from 'store/global/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';
import { selectLoanRolesValues } from 'store/system/selectors';
import { selectExtendedUsersValues } from 'store/users/selectors';

import { selectUnderwriting } from './underwriting.selectors';

export const selectSlaGroups = createSelector(selectUnderwriting, ({ slaGroups }) => slaGroups);

const selectSlaGroupsValues = createSelector(selectSlaGroups, Object.values);

export const selectSlaGroupById = memoizeOne(id =>
  createSelector(selectSlaGroups, slaGroups => slaGroups[id]),
);

export const selectAllRolesAndUsers = createSelector(
  [selectRolesList, selectExtendedUsersValues, selectLoanRolesValues],
  (securityGroups, users, loanRoles) => ({
    [SlaLevelType.LOAN_ROLE]: loanRoles,
    [SlaLevelType.SECURITY_GROUP]: securityGroups,
    [SlaLevelType.USER]: users,
  }),
);

export const selectAllRolesAndUsersById = createSelector(
  [selectRolesList, selectExtendedUsersValues, selectLoanRolesValues],
  (securityGroups, users, loanRoles) => {
    const allRolesAndUsers = [...securityGroups, ...users, ...loanRoles];

    return allRolesAndUsers.reduce((acc, item) => {
      const id = item.id || item.loanRoleId;
      acc[id] = item;

      return acc;
    }, {});
  },
);

const selectSlaGroupsByNotDeletedRoles = createSelector(
  [selectAllRolesAndUsersById, selectSlaGroupsValues],
  (allRolesAndUsersById, slaGroups) =>
    slaGroups.reduce((acc, group) => {
      const notDeletedRoles = group.levels.filter(
        ({ assignmentRule }) => !allRolesAndUsersById[assignmentRule.id]?.isDeleted,
      );

      if (notDeletedRoles.length) {
        acc.push({ ...group, levels: notDeletedRoles });
      }

      return acc;
    }, []),
);

export const selectFilteredSlaGroups = createSelector(
  [getDebouncedSearchValue, selectSlaGroupsValues],
  searchItems(({ name }) => [name]),
);

export const selectSlaGroupsOptions = createSelector(selectSlaGroupsByNotDeletedRoles, values =>
  values.map(({ name, id, isGlobal }) => ({ name, value: id, isGlobal })),
);

export const selectLocalSlaGroupsOptions = createSelector(selectSlaGroupsOptions, values =>
  values.filter(({ name, isGlobal }) => !isGlobal || name === DENIED_SLA_GROUP_NAME),
);
